/**
 * Guards for the photo-upload flow.
 *
 * Two failures this covers, both of which are currently silent:
 *
 *  1. `localStorage.setItem` with a multi-megabyte base64 photo throws
 *     synchronously once the origin is near its ~5MB cap. In
 *     sections/image-upload.liquid that throw happens inside an onload
 *     handler, so the loader overlay never hides and the customer sits on a
 *     spinner forever.
 *  2. `file.type.startsWith("image")` has no size check and no else branch —
 *     pick a PDF or a 60MB clip and literally nothing happens on screen.
 *
 * Plain script, no build step: attaches window.ImageUploadSafety in the theme
 * and exports for node --test.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.ImageUploadSafety = api;
})(typeof self !== 'undefined' ? self : this, function () {
  // Deliberately more permissive than the 10MB the upload API accepts: the
  // browser downscales to 2000px/q0.7 before anything is POSTed, so a 20MB
  // original from a 48MP camera is fine. This ceiling only catches input that
  // could never be a photo the customer meant to send.
  var MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

  // Exact match, not a prefix test. Mirrors the API allowlist in
  // TellMyTale-Klaviyo-Integration-API/utils/uploadValidation.js so the two
  // ends cannot disagree. image/svg+xml stays out: the bucket is public-read.
  var ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
    'image/heif'
  ];

  function megabytes(bytes) {
    return Math.round(bytes / (1024 * 1024));
  }

  /**
   * Writes to storage without ever throwing.
   * @returns {{ok: true} | {ok: false, reason: 'quota'|'unavailable'}}
   */
  function safeSetItem(storage, key, value) {
    if (!storage) return { ok: false, reason: 'unavailable' };
    try {
      storage.setItem(key, value);
      return { ok: true };
    } catch (err) {
      var name = err && err.name;
      var isQuota =
        name === 'QuotaExceededError' ||
        name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
        (err && err.code === 22);
      return { ok: false, reason: isQuota ? 'quota' : 'unavailable' };
    }
  }

  /**
   * @returns {{ok: true} | {ok: false, reason: 'no_file'|'wrong_type'|'too_large', message: string}}
   */
  function validateImageFile(file, options) {
    var maxBytes = (options && options.maxBytes) || MAX_UPLOAD_BYTES;

    if (!file) {
      return { ok: false, reason: 'no_file', message: 'Please choose a photo to upload.' };
    }
    if (ALLOWED_TYPES.indexOf(file.type) === -1) {
      return {
        ok: false,
        reason: 'wrong_type',
        message: 'That file is not a photo we can use. Please upload a JPEG, PNG, WebP or HEIC image.'
      };
    }
    if (file.size > maxBytes) {
      return {
        ok: false,
        reason: 'too_large',
        message: 'That photo is too large (' + megabytes(file.size) + 'MB). Please choose one under ' + megabytes(maxBytes) + 'MB.'
      };
    }
    return { ok: true };
  }

  return {
    safeSetItem: safeSetItem,
    validateImageFile: validateImageFile,
    MAX_UPLOAD_BYTES: MAX_UPLOAD_BYTES,
    ALLOWED_TYPES: ALLOWED_TYPES
  };
});
